// Canvas の盤面（mch-dice の render.js を移植）。
// 描画は自前の表示用モデル（vm）だけを見る。vm はイベント再生でだけ動くので、
// ルール上はもう決着していても、演出は起きた順に見える。再生の最後に sync() で正本に合わせる。
import type { GroundKind, Side, StatusKey } from '@hyakki/core';
import { attackRecipe, castRecipe, ougiRecipe, shutenSkillRecipe, buildRecipe, captureRecipe, chainBlastRecipe, coreRecipe, drawParticles, healRecipe, koRecipe, splashRecipe, type FxKind, type Particle } from './fx';
import { AWAKE_SPRITES, BUILDING_INFO, DECOR_BY_TERRAIN, HERO_SPRITES, ROLE_STYLE, WORLD, buildingSprite, enemyBuildingSprite, drawEnemyPlaceholder, drawSprite, drawSpriteTinted, firstLoaded, enemySprite, relaySprite, sheet, leaderSprite } from '../assets';
import type { VBoard, VUnit } from './adapter';
import { CELLS, ROWS, cellX, dist } from './hex';
import { icon } from '../ui/icons';

/** 状態異常の印（字は1文字。色だけに頼らない） */
export const STATUS_MARK: Record<StatusKey, { ch: string; bg: string; good?: boolean }> = {
  burn: { ch: '火', bg: '#d8502a' },
  bind: { ch: '縛', bg: '#4f7fb8' },
  seal: { ch: '封', bg: '#7a52a8' },
  curse: { ch: '呪', bg: '#3c3346' },
  poison: { ch: '毒', bg: '#4a8a3a' },
  might: { ch: '剛', bg: '#c98a1c', good: true },
  ward: { ch: '守', bg: '#2f7f6a', good: true },
  blessed: { ch: '清', bg: '#b58a2a', good: true },
};

/** 土地ギミックの見た目（霊泉・神域・泥濘・毒沼） */
export const GROUND_MARK: Record<GroundKind, { ch: string; fill: string; rim: string }> = {
  spring: { ch: '泉', fill: 'rgba(60,170,220,0.5)', rim: '#1f78a8' },
  sacred: { ch: '神', fill: 'rgba(245,195,60,0.5)', rim: '#a87c10' },
  mire: { ch: '泥', fill: 'rgba(105,72,38,0.6)', rim: '#5a3a1a' },
  miasma: { ch: '毒', fill: 'rgba(120,70,160,0.5)', rim: '#5e2f86' },
};

const SQ3 = Math.sqrt(3);

const TERRAIN: Record<string, { base: string; alt: string; edge: string; block: string }> = {
  meadow: { base: '#6f8f45', alt: '#7a9a4c', edge: '#2e3f1f', block: '#4c5a33' },
  river: { base: '#6c8d5d', alt: '#78996a', edge: '#21384a', block: '#3e6a86' },
  bamboo: { base: '#6a8a40', alt: '#759548', edge: '#24361a', block: '#3f6a2a' },
  temple: { base: '#8b7a58', alt: '#958463', edge: '#3a3020', block: '#5d4c38' },
  snow: { base: '#c9d2d8', alt: '#d6dde2', edge: '#5d6a75', block: '#8592a0' },
  volcano: { base: '#6b4a3a', alt: '#755242', edge: '#2a1810', block: '#3d2620' },
  grave: { base: '#5a6158', alt: '#646b61', edge: '#20241f', block: '#3a3f3a' },
  mountain: { base: '#7a8456', alt: '#848e5f', edge: '#2e3322', block: '#545a45' },
  oni: { base: '#7a5048', alt: '#845850', edge: '#2e1612', block: '#4d2a24' },
  capital: { base: '#8a7a64', alt: '#94846d', edge: '#2e261c', block: '#5b4d3c' },
};
// 陣地の塗り：自陣＝青・敵陣＝朱の半透明（地面プレートが透けて見える濃さ）
const OWNER_FILL: Record<Side, string> = { blue: 'rgba(56,132,245,0.34)', red: 'rgba(232,64,44,0.32)' };
const OWNER_RIM: Record<Side, string> = { blue: 'rgba(160,210,255,0.75)', red: 'rgba(255,165,140,0.75)' };
const SIDE_COLOR: Record<Side | 'neutral', string> = { blue: '#4f9cff', red: '#ff5d4a', neutral: '#e8b24a' };
const HP_COLOR: Record<Side | 'neutral', string> = { blue: '#5cc8ff', red: '#ff5a48', neutral: '#ffb347' };
/** 自陣の中の光（RGB） */
const TERR_GLOW: Record<Side | 'neutral', string> = { blue: '120,196,255', red: '255,150,110', neutral: '255,210,120' };

// ---------- 妖力の炎（足元のゲージ・オーラ） ----------
/** 上向きの炎の形（先端 y=-1、底 y≈0.25、幅 ±0.4）。translate＋scale で使い回す */
const FLAME = new Path2D('M0,-1 C0.16,-0.64 0.44,-0.46 0.42,-0.14 C0.4,0.14 0.22,0.26 0,0.26 C-0.22,0.26 -0.4,0.14 -0.42,-0.14 C-0.44,-0.46 -0.16,-0.64 0,-1 Z');
const FLAME_CORE = new Path2D('M0,-0.5 C0.1,-0.3 0.22,-0.2 0.2,0 C0.18,0.14 0.1,0.18 0,0.18 C-0.1,0.18 -0.18,0.14 -0.2,0 C-0.22,-0.2 -0.1,-0.3 0,-0.5 Z');
/** 段階ごとの炎の色（外・芯）。壱＝橙の小さな灯、弐＝青白い妖火、参＝金と朱 */
/** 妖力の炎と妖気。弐＝青紫の鬼火、参＝紅と紫の妖炎。光らせるのではなく、黒紫の妖気を纏わせる。
 *  glow：妖気のもや（中心・縁の色・外側） */
export const SURGE_FIRE: Record<number, { outer: string; alt: string; core: string; glow: [string, string, string] }> = {
  1: { outer: '#ffb85a', alt: '#ffb85a', core: '#fff1c8', glow: ['rgba(0,0,0,0)', 'rgba(0,0,0,0)', 'rgba(0,0,0,0)'] },
  2: { outer: '#6f5cff', alt: '#3fa8ff', core: '#d8d0ff', glow: ['rgba(26,8,48,0.62)', 'rgba(96,60,220,0.32)', 'rgba(40,20,90,0)'] },
  3: { outer: '#d81e48', alt: '#9a3cff', core: '#ffb8a0', glow: ['rgba(26,0,24,0.66)', 'rgba(150,20,120,0.42)', 'rgba(70,0,60,0)'] },
};

/** 結界が割れる演出の長さ（ms） */
const BARRIER_BREAK_MS = 1100;

export interface VMUnit extends VUnit {
  alpha: number;
  flash: number;
  ox: number;
  oy: number;
  px: number | null;
  py: number | null;
}

export interface Highlights {
  reach: Set<number> | null;
  build: Set<number> | null;
  path: number[] | null;
  sel: number | null;
  targets: { cell: number; core: boolean; selected: boolean }[];
  previews: { cell: number; hp: number; after: number; max: number; selected: boolean; splash?: boolean }[];
  ghost: { cell: number; unit: VUnit } | null;
  info: number | null; // 情報カードを開いているマス
  /** 一撃離脱の移動範囲（色を変える） */
  dash?: boolean;
  /** 術：選べるマス（縮地の着地点・送り火のマス）と、効く範囲の下見 */
  castCells?: Set<number> | null;
  castArea?: Set<number> | null;
}
export const emptyHighlights = (): Highlights => ({ reach: null, build: null, path: null, sel: null, targets: [], previews: [], ghost: null, info: null, dash: false });

interface Effect {
  cell: number;
  from: number;
  kind: 'slash' | 'shot' | 'burst' | 'skill' | 'heal' | 'capture' | 'build' | 'surge';
  color: string;
  start: number;
  ms: number;
}
interface Anim {
  start: number;
  ms: number;
  fn: (t: number) => void;
  resolve: () => void;
}

export class BoardView {
  readonly ctx: CanvasRenderingContext2D;
  w = 0;
  h = 0;
  s = 20; // ヘックスの半径
  hw = 0;
  ox = 0;
  oy = 0;
  speed = 1;
  reduced = false;
  vm: { board: VBoard; units: Map<number, VMUnit>; owners: (Side | null)[]; cores: Record<Side, number>; collapse: Partial<Record<Side, number>> } | null = null;
  hl: Highlights = emptyHighlights();
  private anims: Anim[] = [];
  private effects: Effect[] = [];
  /** 絵のエフェクト（fx.webp の部品。battle/fx.ts のレシピ） */
  private parts: Particle[] = [];
  private attackFocus: { id: number; cell: number; until: number } | null = null;
  private shakeA = 0;
  private shakeStart = 0;
  private shakeMs = 380;
  private shakeSeed = 0;
  private bg: HTMLCanvasElement | null = null;
  private bgKey = '';
  /** 敵の城の結界：見えている度合い（0..1）と、割れた時刻 */
  private barrierVis: Record<Side, number> = { blue: 0, red: 0 };
  private barrierAt: Record<Side, number> = { blue: -1e9, red: -1e9 };
  /** オーラの光（放射グラデーション）は段階×大きさごとに一度だけ焼いておく */
  private glowCache = new Map<string, HTMLCanvasElement>();
  /** 自陣に入った時刻（入った瞬間の波紋用）。出たら消す */
  private insideAt = new Map<number, number>();

  constructor(
    readonly cv: HTMLCanvasElement,
    readonly floaters: HTMLElement,
  ) {
    this.ctx = cv.getContext('2d')!;
    this.resize();
  }

  /** 盤面に使えない縁（上下の HUD・PC の左右の欄）。Canvas は画面全体に敷き、盤はこの内側に置く */
  insets = { t: 0, r: 0, b: 0, l: 0 };
  setInsets(t: number, r: number, b: number, l: number) {
    const n = { t: Math.max(0, t), r: Math.max(0, r), b: Math.max(0, b), l: Math.max(0, l) };
    const o = this.insets;
    if (o.t === n.t && o.r === n.r && o.b === n.b && o.l === n.l) return false;
    this.insets = n;
    this.resize();
    return true;
  }

  resize() {
    const r = this.cv.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    this.w = Math.max(1, r.width);
    this.h = Math.max(1, r.height);
    this.cv.width = Math.round(this.w * dpr);
    this.cv.height = Math.round(this.h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    // 盤面（7×√3 × 17 の縦長）が空き領域に収まる半径。上は敵本陣のHPの分（0.7）だけ余白を取る
    const fw = Math.max(40, this.w - this.insets.l - this.insets.r);
    const fh = Math.max(40, this.h - this.insets.t - this.insets.b);
    // 縦画面の左右端にも、覚醒キャラと踏み込みの分だけ描画余白を残す。
    const s = Math.min((fw - 4) / (7 * SQ3 + (this.w < 600 ? 2 : 0.25)), (fh - 4) / (2 + (ROWS - 1) * 1.5 + 0.75));
    this.s = s;
    this.hw = SQ3 * s;
    this.ox = this.insets.l + (fw - 7 * this.hw) / 2;
    this.oy = this.insets.t + (fh - (2 + (ROWS - 1) * 1.5) * s) / 2 + s * 0.3;
    this.bg = null;
    this.glowCache.clear();
  }

  center(i: number) {
    const c = CELLS[i];
    return { x: this.ox + (cellX(c.col, c.row) + 0.5) * this.hw, y: this.oy + this.s + c.row * 1.5 * this.s };
  }

  cellAt(px: number, py: number): number | null {
    let best: number | null = null;
    let bd = Infinity;
    for (const c of CELLS) {
      const p = this.center(c.i);
      const d = (p.x - px) ** 2 + (p.y - py) ** 2;
      if (d < bd) {
        bd = d;
        best = c.i;
      }
    }
    return bd <= this.s * this.s * 1.05 ? best : null;
  }

  // ---------- 表示用モデル ----------
  sync(board: VBoard) {
    const units = new Map<number, VMUnit>();
    for (const u of board.units) units.set(u.id, { ...u, alpha: u.alive ? 1 : 0, flash: 0, ox: 0, oy: 0, px: null, py: null });
    this.vm = {
      board,
      units,
      owners: board.tiles.map((t) => t.owner),
      cores: { blue: board.cores.blue.hp, red: board.cores.red.hp },
      collapse: {},
    };
  }
  unit(id: unknown) {
    return typeof id === 'number' ? this.vm?.units.get(id) : undefined;
  }
  unitPos(u: VMUnit) {
    if (u.px != null && u.py != null) return { x: u.px, y: u.py };
    const p = this.center(u.cell ?? 0);
    return { x: p.x + u.ox, y: p.y + u.oy };
  }

  // ---------- 演出 ----------
  /** 攻撃元と狙い先を先に示す。光量を増やさず、視線を着弾先へ誘導する。 */
  focusAttack(id: number, cell: number) {
    this.attackFocus = { id, cell, until: performance.now() + 1500 / this.speed };
  }

  private drawAttackFocus(ctx: CanvasRenderingContext2D, now: number) {
    const f = this.attackFocus;
    if (!f || now > f.until) return;
    const u = this.unit(f.id);
    if (!u || u.cell == null) return;
    const a = this.unitPos(u);
    const b = this.center(f.cell);
    ctx.save();
    ctx.strokeStyle = u.side === 'blue' ? '#c6efff' : '#ffb6a5';
    ctx.lineWidth = Math.max(2, this.s * 0.045);
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
    ctx.setLineDash([]);
    this.hexPath(ctx, b.x, b.y, this.s * 0.9);
    ctx.stroke();
    // 四隅の短い線は縮小表示でも狙い先として読める。
    for (const [dx, dy] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const x = b.x + dx * this.s * 0.8;
      const y = b.y + dy * this.s * 0.8;
      ctx.beginPath();
      ctx.moveTo(x - dx * this.s * 0.22, y);
      ctx.lineTo(x, y);
      ctx.lineTo(x, y - dy * this.s * 0.22);
      ctx.stroke();
    }
    ctx.restore();
  }

  /** フレームで進む補間。タブ非表示などでフレームが止まっても、タイマーで必ず終わらせる。 */
  tween(ms: number, fn: (t: number) => void): Promise<void> {
    return new Promise((resolve) => {
      const a: Anim = { start: performance.now(), ms: Math.max(1, ms / this.speed), fn, resolve };
      this.anims.push(a);
      setTimeout(() => this.finish(a), a.ms + 150);
    });
  }
  private finish(a: Anim) {
    const k = this.anims.indexOf(a);
    if (k < 0) return;
    this.anims.splice(k, 1);
    a.fn(1);
    a.resolve();
  }
  /** 一時停止・中断時：進行中の補間をすべて終点へ */
  flushAnims() {
    for (const a of this.anims.slice()) this.finish(a);
  }
  wait(ms: number) {
    return this.tween(ms, () => {});
  }

  async moveUnit(id: number, path: number[], painted: number[], side: Side | null) {
    const u = this.unit(id);
    if (!u || path.length < 1) return;
    const paint = new Set(painted);
    for (let k = 1; k < path.length; k++) {
      const a = this.center(path[k - 1]);
      const b = this.center(path[k]);
      await this.tween(80, (t) => {
        const e = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
        u.px = a.x + (b.x - a.x) * e;
        u.py = a.y + (b.y - a.y) * e - Math.sin(t * Math.PI) * this.s * 0.25;
      });
      if (side && paint.has(path[k])) this.vm!.owners[path[k]] = side;
    }
    u.px = u.py = null;
    u.cell = path[path.length - 1];
  }

  /** 攻撃の絵の種類：キャラ（ヒーロー・宿敵）は各自の攻撃と術、妖は近接＝爪・遠距離＝妖気の弾。建物（櫓）は従来の矢 */
  /** 攻撃の見た目の種類（音を見た目に合わせるため。建物・不明は null） */
  attackKind(id: number): FxKind | null {
    const u = this.unit(id);
    return u ? this.fxKind(u) : null;
  }
  private fxKind(u: VMUnit): FxKind | null {
    if (u.kind === 'building') return null;
    if (u.charId) return u.charId;
    // 酒呑童子（プロローグ）の通常攻撃はスズネと同じ打撃の絵。術・奥義は ougi_shuten
    if (u.fx) return u.fx === 'shuten' ? 'suzune' : u.fx;
    return (u.stats.range ?? 1) > 1 ? 'orb' : 'claw';
  }
  /** 体の中心（足元より少し上） */
  private bodyAt(cell: number) {
    const p = this.center(cell);
    return { x: p.x, y: p.y - this.s * 0.35 };
  }

  /** 攻撃の演出。onHit は当たった瞬間に呼ぶ（音を当たりに合わせる。奥義は当たった後の余韻があるので終わりを待たない） */
  async attackFx(id: number, cell: number, skill: boolean, ougi = false, onHit?: () => void) {
    const u = this.unit(id);
    if (!u || u.cell == null) return onHit?.();
    const kind = this.fxKind(u);
    const oc = u.charId ?? u.fx;
    if (ougi && oc && sheet(`ougi_${oc}`) && !this.reduced) return this.ougiAttack(u, oc, cell, onHit);
    if (skill && oc === 'shuten' && sheet('ougi_shuten') && !this.reduced) return this.shutenSkill(u, cell, onHit);
    if (kind && sheet('fx') && !this.reduced) return void (await this.spriteAttack(u, kind, cell, skill), onHit?.());
    const ranged = (u.stats.range ?? 1) > 1 || u.kind === 'building';
    const color = skill ? '#ffcf5a' : u.side === 'blue' ? '#bfe3ff' : '#ffb0a0';
    if (ranged) {
      this.effects.push({ cell, from: u.cell, kind: skill ? 'skill' : 'shot', color, start: performance.now(), ms: 420 / this.speed });
      await this.wait(this.reduced ? 80 : 180);
    } else {
      const a = this.center(u.cell);
      const b = this.center(cell);
      const dx = (b.x - a.x) * 0.35;
      const dy = (b.y - a.y) * 0.35;
      await this.tween(90, (t) => {
        u.ox = dx * t;
        u.oy = dy * t;
      });
      this.effects.push({ cell, from: u.cell, kind: skill ? 'skill' : 'slash', color, start: performance.now(), ms: 360 / this.speed });
      await this.tween(110, (t) => {
        u.ox = dx * (1 - t);
        u.oy = dy * (1 - t);
      });
    }
    if (skill) this.shake(6);
    onHit?.();
  }

  /** 絵のエフェクトの攻撃：近接は踏み込んでから、飛び道具は当たるまで待つ */
  private async spriteAttack(u: VMUnit, kind: FxKind, cell: number, skill: boolean) {
    const from = u.cell!;
    const k = 1 / this.speed;
    const a = this.bodyAt(from);
    const b = this.bodyAt(cell);
    const melee = (u.stats.range ?? 1) <= 1 && kind !== 'orb';
    if (melee) {
      const dx = (b.x - a.x) * 0.35;
      const dy = (b.y - a.y) * 0.35;
      await this.tween(90, (t) => {
        u.ox = dx * t;
        u.oy = dy * t;
      });
      const r = attackRecipe(kind, skill, a, b, this.s, performance.now(), k);
      this.parts.push(...r.parts);
      await this.tween(110, (t) => {
        u.ox = dx * (1 - t);
        u.oy = dy * (1 - t);
      });
      if (r.hitAt > 110) await this.wait(r.hitAt - 110);
    } else {
      const r = attackRecipe(kind, skill, a, b, this.s, performance.now(), k);
      this.parts.push(...r.parts);
      await this.wait(r.hitAt);
    }
    if (skill) this.shake(kind === 'suzune' ? 9 : 6);
  }
  /** 妖力の奥義：盤を暗くし、奥義の絵（ougi_<キャラ>）のコマ送りと部品を飛ばす。当たる時に大きく揺らす */
  /** 術（自分を中心に放つもの）のエフェクト。cells＝効く範囲。絵が無い・動きを減らす設定なら輪だけ */
  async castFx(jutsu: string, id: number, cells: number[], ougi: boolean, onHit?: () => void) {
    const u = this.unit(id);
    if (!u || u.cell == null) return onHit?.();
    const a = this.bodyAt(u.cell);
    const pts = cells.map((c) => this.bodyAt(c));
    // 範囲の中心（送り火は選んだマス＝範囲の真ん中のマス）
    const mid = cells.length ? this.bodyAt(cells[Math.floor(cells.length / 2)]) : a;
    const at = jutsu === 'okuribi' && cells.length ? this.bodyAt(centerCell(cells)) : jutsu === 'shukuchi' && cells.length ? this.bodyAt(cells[0]) : mid;
    if (!sheet('fxJutsu') || this.reduced) {
      this.burst(u.cell, 'skill', '#ffcf5a');
      return onHit?.();
    }
    const r = castRecipe(jutsu, a, jutsu === 'toro' || jutsu === 'tsumuji' || jutsu === 'shuzen' || jutsu === 'gekka' || jutsu === 'kitsunebijin' ? a : at, pts, this.s, performance.now(), 1 / this.speed, ougi);
    this.parts.push(...r.parts);
    this.flash(id);
    await this.wait(r.hitAt);
    if (ougi) this.shake(10);
    onHit?.();
  }
  private async shutenSkill(u: VMUnit, cell: number, onHit?: () => void) {
    const r = shutenSkillRecipe(this.bodyAt(u.cell!), this.bodyAt(cell), this.s, performance.now(), 1 / this.speed);
    this.parts.push(...r.parts);
    this.flash(u.id);
    await this.wait(r.hitAt);
    this.shake(6);
    onHit?.();
    await this.wait(250);
  }
  private async ougiAttack(u: VMUnit, kind: import('@hyakki/core').CharId | 'shuten', cell: number, onHit?: () => void) {
    const k = 1 / this.speed;
    const now = performance.now();
    const r = ougiRecipe(kind, this.bodyAt(u.cell!), this.bodyAt(cell), this.s, now, k);
    this.parts.push(...r.parts);
    this.dim = { t0: now, ms: (r.hitAt + 1300) * k };
    this.flash(u.id);
    await this.wait(r.hitAt);
    this.shake(14);
    onHit?.();
    // 余韻：大技を見せてから次へ
    await this.wait(650);
  }
  /** 奥義の間、盤を暗くする（開始と長さ） */
  private dim: { t0: number; ms: number } | null = null;
  /** 回復（吸収・薬・鳥居）：緑のきらめき。絵が無いときは従来の輪 */
  heal(cell: number) {
    if (sheet('fx') && !this.reduced) this.parts.push(...healRecipe(this.bodyAt(cell), this.s, performance.now(), 1 / this.speed));
    else this.burst(cell, 'heal', '#8cf0a8');
  }
  private fxOn() {
    return !!sheet('fx') && !this.reduced;
  }
  /** 撃破：閃光・衝撃の輪・魂の光（ボス・宿敵は大きく）。false なら絵が無い（従来の輪で描く） */
  ko(cell: number, big: boolean): boolean {
    if (!this.fxOn()) return false;
    this.parts.push(...koRecipe(this.bodyAt(cell), this.s, performance.now(), 1 / this.speed, big));
    if (big) this.shake(9);
    return true;
  }
  /** 城の結界が破れた（番人を倒した側の相手の城）：結界が割れて破片が飛び散る */
  barrierBreak(side: Side) {
    this.barrierAt[side] = performance.now();
    this.barrierVis[side] = 0;
    if (!this.reduced) this.shake(7);
  }
  /** 本陣への攻撃（fall＝攻め落とした） */
  coreFx(cell: number, fall: boolean) {
    if (!this.fxOn()) return;
    this.parts.push(...coreRecipe(this.bodyAt(cell), this.s, performance.now(), 1 / this.speed, fall));
  }
  /** 龍脈の占領 */
  capture(cell: number) {
    if (this.fxOn()) this.parts.push(...captureRecipe(this.bodyAt(cell), this.s, performance.now(), 1 / this.speed));
    else this.burst(cell, 'capture', '#f7cf69');
  }
  /** 建築 */
  built(cell: number) {
    if (this.fxOn()) this.parts.push(...buildRecipe(this.bodyAt(cell), this.s, performance.now(), 1 / this.speed));
    else this.burst(cell, 'build', '#ffd27a');
  }
  /** 術の巻き添え（範囲ダメージ） */
  splash(cell: number) {
    if (sheet('fx') && !this.reduced) this.parts.push(...splashRecipe(this.bodyAt(cell), this.s, performance.now(), 1 / this.speed));
    else this.burst(cell, 'burst', '#ffbf69');
  }

  burst(cell: number, kind: Effect['kind'], color: string) {
    this.effects.push({ cell, from: cell, kind, color, start: performance.now(), ms: 520 / this.speed });
  }
  flash(id: number) {
    const u = this.unit(id);
    if (u && !this.reduced) u.flash = 1;
  }
  /** 妖力の段階が上がった：表示用の段階を先に上げ、足元から炎の輪を広げる */
  surgeUp(id: number, stage: number) {
    const u = this.unit(id);
    if (!u) return;
    u.surge = stage;
    if (u.cell != null) this.effects.push({ cell: u.cell, from: u.cell, kind: 'surge', color: SURGE_FIRE[stage]?.outer ?? '#ffd35a', start: performance.now(), ms: 900 / this.speed });
    this.flash(id);
  }
  /** 揺れ：時間で減衰するなめらかな揺れ（コマごとの乱数だと、画面がブレて見える）。続けて呼ばれたら強い方を残す */
  shake(a = 6, ms = 380) {
    if (this.reduced) return;
    const now = performance.now();
    const left = this.shakeA * Math.max(0, 1 - (now - this.shakeStart) / this.shakeMs);
    if (a < left) return;
    this.shakeA = a;
    this.shakeStart = now;
    this.shakeMs = ms;
    this.shakeSeed = Math.random() * 100;
  }
  /** 揺れを止める（勝敗の帯を出す前に盤を落ち着かせる） */
  stopShake() {
    this.shakeA = 0;
  }

  /** 直近の文字の位置（同じ所に続けて出たら上にずらして重ねない） */
  private recentPops: { x: number; y: number; t: number }[] = [];
  /** 回復の浮き文字：❤アイコン＋量だけ（出どころはログに書く） */
  popupHeal(id: number, amount: number) {
    this.popupUnit(id, `+${amount}`, 'heal', icon('heart'));
  }
  popup(x: number, y: number, text: string, cls = 'dmg', lead = '') {
    const now = performance.now();
    this.recentPops = this.recentPops.filter((p) => now - p.t < 650);
    const near = this.recentPops.filter((p) => Math.abs(p.x - x) < this.s * 1.2 && Math.abs(p.y - y) < this.s * 1.6).length;
    this.recentPops.push({ x, y, t: now });
    y -= near * Math.max(20, this.s * 0.75);
    const el = document.createElement('div');
    el.className = `floater ${cls}`;
    el.textContent = text;
    if (lead) el.insertAdjacentHTML('afterbegin', lead);
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    el.style.animationDuration = `${0.95 / Math.max(1, this.speed * 0.75)}s`;
    this.floaters.appendChild(el);
    const margin = Math.min(this.w / 2, el.offsetWidth * 0.62 + 8);
    el.style.left = `${Math.max(margin, Math.min(this.w - margin, x))}px`;
    el.style.top = `${Math.max(this.insets.t + el.offsetHeight, y)}px`;
    setTimeout(() => el.remove(), 1300);
  }
  popupAt(cell: number, text: string, cls?: string) {
    const p = this.center(cell);
    this.popup(p.x, p.y - this.s * 0.9, text, cls);
  }
  popupUnit(id: number, text: string, cls?: string, lead = '') {
    const u = this.unit(id);
    if (!u || u.cell == null) return;
    const p = this.unitPos(u);
    this.popup(p.x, p.y - this.s * 1.2, text, `${cls ?? 'dmg'} ${u.side}`, lead);
  }

  async collapseCore(side: Side) {
    const vm = this.vm!;
    // 崩れる間、本陣のあちこちで小爆発が連鎖する
    if (this.fxOn()) this.parts.push(...chainBlastRecipe(this.bodyAt(vm.board.cores[side].cell), this.s, performance.now(), 1 / this.speed));
    // 崩れる間はだんだん強くなる地鳴り（1本のなめらかな揺れ）→ 崩れ切ったら静める
    this.shake(5, 500);
    let next = 0.35;
    await this.tween(this.reduced ? 300 : 1100, (t) => {
      vm.collapse[side] = t;
      if (t >= next) {
        this.shake(5 + 7 * t, 500);
        next += 0.35;
      }
    });
    // 揺れが収まってから勝敗の帯へ（揺れたまま帯が出るとブレて見える）
    await this.wait(this.reduced ? 0 : 260);
    this.stopShake();
  }

  // ---------- 描画 ----------
  frame(now: number) {
    for (const a of this.anims.slice()) {
      const t = Math.min(1, (now - a.start) / a.ms);
      if (t >= 1) this.finish(a);
      else a.fn(t);
    }
    const ctx = this.ctx;
    ctx.save();
    ctx.clearRect(0, 0, this.w, this.h);
    if (!this.vm) {
      ctx.restore();
      return;
    }
    if (this.shakeA > 0) {
      const k = (now - this.shakeStart) / this.shakeMs;
      if (k >= 1) this.shakeA = 0;
      else {
        // 減衰（後半ほど急に）× 2つの周波数のなめらかな揺れ（コマの速さに関係なく同じ動き）
        const amp = this.shakeA * (1 - k) * (1 - k) * 0.5;
        const t = (now - this.shakeStart) / 1000;
        const z = this.shakeSeed;
        ctx.translate(Math.round(amp * Math.sin(t * 47 + z) * 2) / 2, Math.round(amp * Math.sin(t * 61 + z * 1.7) * 2) / 2);
      }
    }
    this.drawBackground(ctx);
    this.drawTiles(ctx);
    this.drawZones(ctx, now);
    this.drawHighlights(ctx, now);
    this.drawWards(ctx);
    this.drawAttackFocus(ctx, now);
    this.drawUnits(ctx, now);
    this.drawEffects(ctx, now);
    // 予測ダメージは相手の HP のゲージの中に出す（別のゲージは出さない）
    ctx.restore();
  }

  private hexPath(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
    ctx.beginPath();
    for (let k = 0; k < 6; k++) {
      const ang = ((60 * k - 30) * Math.PI) / 180;
      const px = x + r * Math.cos(ang);
      const py = y + r * Math.sin(ang);
      if (k) ctx.lineTo(px, py);
      else ctx.moveTo(px, py);
    }
    ctx.closePath();
  }

  /** 盤面（ヘックス全体を囲む矩形） */
  private boardRect() {
    const s = this.s;
    const x0 = this.ox - s * 0.35;
    const y0 = this.oy - s * 0.35;
    const x1 = this.ox + 7 * this.hw + s * 0.35;
    const y1 = this.oy + (2 + (ROWS - 1) * 1.5) * s + s * 0.35;
    return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
  }

  /** 戦場の地面は画面全体に“地続き”で敷く（枠は描かない）。
   *  全画面用の背景（art/battlefield）があればそれを、無ければ地面プレートをぼかして全面に広げ、盤の下だけ縁をぼかした鮮明な絵を重ねる。 */
  private drawBackground(ctx: CanvasRenderingContext2D) {
    const terrain = this.vm!.board.terrain;
    const th = TERRAIN[terrain] ?? TERRAIN.meadow;
    const field = sheet(`field_${terrain}`);
    const plate = sheet(`plate_${terrain}`) ?? sheet('plate_meadow');
    const decor = sheet('decor');
    const key = `${terrain}:${this.w}x${this.h}:${this.ox},${this.oy},${this.s}:${field ? 1 : 0}${plate ? 1 : 0}${decor ? 1 : 0}`;
    if (!this.bg || this.bgKey !== key) {
      // 地面・格子・障害物は1枚の画像に焼いておく（毎フレーム描かない）
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cv = document.createElement('canvas');
      cv.width = Math.ceil(this.w * dpr);
      cv.height = Math.ceil(this.h * dpr);
      const g = cv.getContext('2d')!;
      g.scale(dpr, dpr);
      const r = this.boardRect();
      g.fillStyle = th.base;
      g.fillRect(0, 0, this.w, this.h);
      const img = field ?? plate;
      if (img) {
        const cx = r.x + r.w / 2;
        const cy = r.y + r.h / 2;
        // 1) 全面：縮小→拡大でぼかした絵（どの縦横比でも隙間なく覆う）
        const cover = Math.max(this.w / img.width, this.h / img.height);
        const tiny = document.createElement('canvas');
        tiny.width = Math.max(8, Math.round(this.w / (field ? 10 : 14)));
        tiny.height = Math.max(8, Math.round(this.h / (field ? 10 : 14)));
        const tg = tiny.getContext('2d')!;
        tg.imageSmoothingQuality = 'high';
        const tk = cover * (tiny.width / this.w);
        tg.drawImage(img, (tiny.width - img.width * tk) / 2, (tiny.height - img.height * tk) / 2, img.width * tk, img.height * tk);
        g.imageSmoothingQuality = 'high';
        g.drawImage(tiny, 0, 0, this.w, this.h);
        g.fillStyle = 'rgba(8,12,20,0.18)';
        g.fillRect(0, 0, this.w, this.h);
        // 2) 鮮明な絵：盤の中心に合わせる
        // 全画面用の背景は常に画面全体を覆う（PCの横長でも帯状にならないように）。プレートは盤より一回り大きく
        const k = field ? Math.max(cover, r.w / img.width, r.h / img.height) : Math.max((r.w * 1.12) / img.width, (r.h * 1.06) / img.height);
        const dw = img.width * k;
        const dh = img.height * k;
        // 画面より大きい向きは、はみ出しても隙間が出ないように寄せる
        const dx = dw >= this.w ? Math.min(0, Math.max(this.w - dw, cx - dw / 2)) : cx - dw / 2;
        const dy = dh >= this.h ? Math.min(0, Math.max(this.h - dh, cy - dh / 2)) : cy - dh / 2;
        const full = dx <= 0.5 && dy <= 0.5 && dx + dw >= this.w - 0.5 && dy + dh >= this.h - 0.5;
        if (full) g.drawImage(img, dx, dy, dw, dh);
        else {
          // 縁をぼかして重ねる（境目を作らない）
          const lay = document.createElement('canvas');
          lay.width = cv.width;
          lay.height = cv.height;
          const lg = lay.getContext('2d')!;
          lg.scale(dpr, dpr);
          lg.drawImage(img, dx, dy, dw, dh);
          lg.globalCompositeOperation = 'destination-in';
          const fx = Math.min(dw * 0.16, this.s * 3);
          const fy = Math.min(dh * 0.1, this.s * 3);
          const x0 = Math.max(dx, 0);
          const x1 = Math.min(dx + dw, this.w);
          const y0 = Math.max(dy, 0);
          const y1 = Math.min(dy + dh, this.h);
          const gx = lg.createLinearGradient(x0, 0, x1, 0);
          const fxa = dx > 0 ? fx / (x1 - x0) : 0;
          const fxb = dx + dw < this.w ? fx / (x1 - x0) : 0;
          gx.addColorStop(0, `rgba(0,0,0,${fxa ? 0 : 1})`);
          gx.addColorStop(Math.min(0.49, fxa), 'rgba(0,0,0,1)');
          gx.addColorStop(Math.max(0.51, 1 - fxb), 'rgba(0,0,0,1)');
          gx.addColorStop(1, `rgba(0,0,0,${fxb ? 0 : 1})`);
          lg.fillStyle = gx;
          lg.fillRect(0, 0, this.w, this.h);
          const gy = lg.createLinearGradient(0, y0, 0, y1);
          const fya = dy > 0 ? fy / (y1 - y0) : 0;
          const fyb = dy + dh < this.h ? fy / (y1 - y0) : 0;
          gy.addColorStop(0, `rgba(0,0,0,${fya ? 0 : 1})`);
          gy.addColorStop(Math.min(0.49, fya), 'rgba(0,0,0,1)');
          gy.addColorStop(Math.max(0.51, 1 - fyb), 'rgba(0,0,0,1)');
          gy.addColorStop(1, `rgba(0,0,0,${fyb ? 0 : 1})`);
          lg.fillStyle = gy;
          lg.fillRect(0, 0, this.w, this.h);
          g.save();
          g.setTransform(1, 0, 0, 1, 0, 0);
          g.drawImage(lay, 0, 0);
          g.restore();
        }
      }
      // 盤の外側をほんの少し暗く（盤の中央に目が行くように。境目は作らない）
      const vg = g.createRadialGradient(r.x + r.w / 2, r.y + r.h / 2, Math.min(r.w, r.h) * 0.45, r.x + r.w / 2, r.y + r.h / 2, Math.max(this.w, this.h) * 0.75);
      vg.addColorStop(0, 'rgba(0,0,0,0)');
      vg.addColorStop(1, 'rgba(6,8,14,0.38)');
      g.fillStyle = vg;
      g.fillRect(0, 0, this.w, this.h);
      // マス：細い半透明の線（影を付けて地面の上でも見えるように）。障害物は少し暗く
      for (const c of CELLS) {
        const t = this.vm!.board.tiles[c.i];
        const p = this.center(c.i);
        this.hexPath(g, p.x, p.y, this.s * 0.985);
        if (t.kind === 'block') {
          g.fillStyle = 'rgba(15,20,10,0.18)';
          g.fill();
        }
        g.strokeStyle = 'rgba(20,16,8,0.22)';
        g.lineWidth = 2.2;
        g.stroke();
        g.strokeStyle = 'rgba(255,250,230,0.42)';
        g.lineWidth = 1;
        g.stroke();
      }
      // 障害物の絵は奥（上）から順に
      for (const c of CELLS) {
        const t = this.vm!.board.tiles[c.i];
        if (t.kind !== 'block') continue;
        const p = this.center(c.i);
        this.drawBlock(g, p.x, p.y, c.i, terrain);
      }
      this.bg = cv;
      this.bgKey = key;
    }
    ctx.drawImage(this.bg, 0, 0, this.w, this.h);
  }

  private drawBlock(g: CanvasRenderingContext2D, x: number, y: number, i: number, terrain: string) {
    const s = this.s;
    const list = DECOR_BY_TERRAIN[terrain] ?? DECOR_BY_TERRAIN.meadow;
    const n = list[((i * 7919) >>> 0) % list.length];
    if (sheet('decor')) {
      // 影
      g.fillStyle = 'rgba(0,0,0,0.3)';
      g.beginPath();
      g.ellipse(x, y + s * 0.42, s * 0.62, s * 0.22, 0, 0, Math.PI * 2);
      g.fill();
      drawSprite(g, { sheet: 'decor', col: n % 4, row: Math.floor(n / 4), foot: 0.06 }, x, y + s * 0.62, s * (n === 0 || n === 1 || n === 5 ? 2.05 : 1.75));
      return;
    }
    // 絵が無いとき：簡易な図形
    if (i % 3 === 0) {
      g.fillStyle = '#2f4a22';
      g.beginPath();
      g.moveTo(x, y - s * 0.75);
      g.lineTo(x + s * 0.5, y + s * 0.25);
      g.lineTo(x - s * 0.5, y + s * 0.25);
      g.closePath();
      g.fill();
    } else {
      g.fillStyle = '#6b6560';
      g.beginPath();
      g.ellipse(x, y + s * 0.1, s * 0.5, s * 0.35, 0, 0, Math.PI * 2);
      g.fill();
    }
  }

  /** 土地ギミック：色の付いた六角と、下の角に1文字（色だけに頼らない） */
  private drawGround(ctx: CanvasRenderingContext2D, kind: GroundKind, x: number, y: number) {
    const s = this.s;
    const m = GROUND_MARK[kind];
    this.hexPath(ctx, x, y, s * 0.9);
    ctx.fillStyle = m.fill;
    ctx.fill();
    ctx.strokeStyle = m.rim;
    ctx.lineWidth = 2.4;
    ctx.stroke();
    // 字はマスの上側（下側はユニットの足元で隠れる）
    const r = s * 0.26;
    const cx = x;
    const cy = y - s * 0.56;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = m.rim;
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.font = `700 ${Math.round(r * 1.3)}px "Shippori Mincho B1", serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(m.ch, cx, cy + r * 0.05);
  }

  private drawTiles(ctx: CanvasRenderingContext2D) {
    const vm = this.vm!;
    const s = this.s;
    for (const c of CELLS) {
      const t = vm.board.tiles[c.i];
      const owner = vm.owners[c.i];
      const { x, y } = this.center(c.i);
      if (owner) {
        this.hexPath(ctx, x, y, s * 0.96);
        ctx.fillStyle = OWNER_FILL[owner];
        ctx.fill();
        this.hexPath(ctx, x, y, s * 0.9);
        ctx.strokeStyle = OWNER_RIM[owner];
        ctx.lineWidth = 1.2;
        ctx.stroke();
      } else if ((t.kind === 'home' || t.kind === 'core') && t.side) {
        this.hexPath(ctx, x, y, s * 0.97);
        ctx.fillStyle = OWNER_FILL[t.side];
        ctx.fill();
      }
      if (t.ground) this.drawGround(ctx, t.ground, x, y);
      if (t.kind === 'camp' || t.kind === 'boss') {
        ctx.setLineDash([4, 3]);
        this.hexPath(ctx, x, y, s * 0.72);
        ctx.strokeStyle = t.kind === 'boss' ? 'rgba(255,215,100,0.7)' : 'rgba(30,15,10,0.5)';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.setLineDash([]);
      }
      if (t.pickup === 'chest' && drawSprite(ctx, { sheet: 'resicons', col: 3, row: 1, foot: 0.12 }, x, y + s * 0.5, s * 1.35)) {
        // 宝箱（宝箱の絵）
      } else if (t.pickup === 'shrine' && drawSprite(ctx, { sheet: 'decor', col: 1, row: 2, foot: 0.06 }, x, y + s * 0.5, s * 1.25)) {
        // 祠（石灯籠の絵）
      } else if (t.pickup) {
        // 祠など（図形）
        ctx.fillStyle = t.pickup === 'chest' ? '#a0642a' : '#e8e0c8';
        ctx.strokeStyle = '#1a1208';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.rect(x - s * 0.28, y - s * 0.2, s * 0.56, s * 0.4);
        ctx.fill();
        ctx.stroke();
        ctx.fillStyle = t.pickup === 'chest' ? '#ffd27a' : '#c8402f';
        ctx.font = `700 ${Math.round(s * 0.32)}px "Shippori Mincho B1", serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(t.pickup === 'chest' ? '宝' : '祠', x, y);
      }
      if (t.kind === 'home' && t.side) {
        // 帰還先の印 
        ctx.strokeStyle = t.side === 'blue' ? 'rgba(160,215,255,0.9)' : 'rgba(255,170,150,0.9)';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(x, y + s * 0.2, s * 0.55, s * 0.22, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }

  /** 術の陣：灯籠陣（金）・狐火の陣（青）。マスを淡く染め、灯籠・狐火の小さな絵を置く */
  private drawZones(ctx: CanvasRenderingContext2D, now: number) {
    const zones = this.vm?.board.zones;
    if (!zones?.length) return;
    const s = this.s;
    const pulse = this.reduced ? 0.6 : 0.5 + 0.5 * Math.sin(now / 420);
    for (const z of zones) {
      const lantern = z.kind === 'lantern';
      for (const c of z.cells) {
        const { x, y } = this.center(c);
        this.hexPath(ctx, x, y, s * 0.92);
        ctx.fillStyle = lantern ? `rgba(255,190,80,${0.22 + 0.08 * pulse})` : `rgba(80,160,255,${0.24 + 0.08 * pulse})`;
        ctx.fill();
        ctx.setLineDash([3, 3]);
        ctx.strokeStyle = lantern ? 'rgba(255,220,140,0.95)' : 'rgba(160,210,255,0.95)';
        ctx.lineWidth = 1.8;
        ctx.stroke();
        ctx.setLineDash([]);
        // 狐火の陣は小さな狐火を角に（灯籠陣の灯籠は drawUnits で駒と一緒に奥行き順に立てる）
        if (!lantern) {
          ctx.globalAlpha = 0.75 + 0.25 * pulse;
          drawSprite(ctx, { sheet: 'fxJutsu', col: 1, row: 3, foot: 0.1 }, x + s * 0.48, y + s * 0.42, s * 0.62);
          ctx.globalAlpha = 1;
        }
      }
    }
  }

  private drawHighlights(ctx: CanvasRenderingContext2D, now: number) {
    const s = this.s;
    const hl = this.hl;
    const pulse = this.reduced ? 0.6 : 0.5 + 0.5 * Math.sin(now / 180);
    const fillCells = (cells: Iterable<number>, fill: string, stroke: string) => {
      for (const cell of cells) {
        const { x, y } = this.center(cell);
        this.hexPath(ctx, x, y, s * 0.9);
        ctx.fillStyle = fill;
        ctx.fill();
        ctx.strokeStyle = stroke;
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    };
    // 移動できるマス：自陣の青と見分けられるよう、白く光る縁がゆっくり明滅する（約2秒で1往復）。一撃離脱は縁を金に
    if (hl.reach) {
      const slow = this.reduced ? 0.6 : 0.5 + 0.5 * Math.sin(now / 320);
      ctx.save();
      ctx.shadowColor = hl.dash ? 'rgba(255,214,90,0.95)' : 'rgba(170,240,255,0.95)';
      ctx.shadowBlur = s * (0.25 + 0.45 * slow);
      fillCells(hl.reach, `rgba(225,250,255,${0.16 + 0.26 * slow})`, hl.dash ? `rgba(255,214,90,${0.7 + 0.3 * slow})` : `rgba(255,255,255,${0.65 + 0.35 * slow})`);
      ctx.restore();
    }
    if (hl.build) fillCells(hl.build, `rgba(255,210,120,${0.14 + 0.08 * pulse})`, 'rgba(255,214,120,0.9)');
    if (hl.castCells) fillCells(hl.castCells, `rgba(185,110,255,${0.3 + 0.12 * pulse})`, 'rgba(235,200,255,1)');
    if (hl.castArea) fillCells(hl.castArea, `rgba(255,120,200,${0.22 + 0.12 * pulse})`, 'rgba(255,190,230,0.95)');
    if (hl.path && hl.path.length > 1) {
      const pts = hl.path.map((c) => this.center(c));
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (const [w, col] of [
        [s * 0.26, 'rgba(10,14,24,0.7)'],
        [s * 0.13, '#ffd35a'],
      ] as const) {
        ctx.beginPath();
        pts.forEach((p, k) => (k ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        ctx.strokeStyle = col;
        ctx.lineWidth = w;
        ctx.stroke();
      }
      ctx.fillStyle = '#fff4c4';
      for (const p of pts.slice(1, -1)) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, s * 0.11, 0, Math.PI * 2);
        ctx.fill();
      }
      const a = pts[pts.length - 2];
      const b = pts[pts.length - 1];
      const ang = Math.atan2(b.y - a.y, b.x - a.x);
      ctx.beginPath();
      ctx.moveTo(b.x + Math.cos(ang) * s * 0.3, b.y + Math.sin(ang) * s * 0.3);
      ctx.lineTo(b.x + Math.cos(ang + 2.4) * s * 0.3, b.y + Math.sin(ang + 2.4) * s * 0.3);
      ctx.lineTo(b.x + Math.cos(ang - 2.4) * s * 0.3, b.y + Math.sin(ang - 2.4) * s * 0.3);
      ctx.closePath();
      ctx.fillStyle = '#ffd35a';
      ctx.fill();
    }
    for (const cell of [hl.sel, hl.info]) {
      if (cell == null) continue;
      const { x, y } = this.center(cell);
      this.hexPath(ctx, x, y, s * 0.92);
      ctx.strokeStyle = cell === hl.sel ? `rgba(255,220,90,${0.6 + 0.4 * pulse})` : 'rgba(255,255,255,0.85)';
      ctx.lineWidth = 3;
      if (cell !== hl.sel) ctx.setLineDash([4, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    // ボスの大技の予告（敵の武将の構えは狙われた武将のマスに、塗らずに赤い枠だけ）
    for (const u of this.vm!.units.values()) {
      if (!u.alive || !u.telegraph) continue;
      for (const cell of u.telegraph) {
        const { x, y } = this.center(cell);
        if (u.kind === 'rival') {
          this.hexPath(ctx, x, y, s * 0.96);
          ctx.strokeStyle = `rgba(255,59,47,${0.55 + 0.45 * pulse})`;
          ctx.lineWidth = 3;
          ctx.setLineDash([6, 4]);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = '#ff3b2f';
          ctx.font = `900 ${Math.round(s * 0.6)}px "Noto Sans JP", sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('!', x + s * 0.62, y - s * 0.62);
          continue;
        }
        this.hexPath(ctx, x, y, s * 0.92);
        ctx.fillStyle = `rgba(255,40,30,${0.22 + 0.3 * pulse})`;
        ctx.fill();
        ctx.strokeStyle = '#ff3b2f';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.fillStyle = '#fff';
        ctx.font = `900 ${Math.round(s * 0.7)}px "Noto Sans JP", sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('!', x, y);
      }
    }
    const cur = this.vm!.board.current;
    const u = cur != null ? this.unit(cur) : undefined;
    if (u && u.alive && u.cell != null && u.px == null) {
      const { x, y } = this.center(u.cell);
      this.hexPath(ctx, x, y, s * 0.86);
      ctx.strokeStyle = `rgba(255,230,120,${0.5 + 0.5 * pulse})`;
      ctx.lineWidth = 2.5;
      ctx.stroke();
    }
  }

  private drawUnits(ctx: CanvasRenderingContext2D, now: number) {
    const vm = this.vm!;
    const s = this.s;
    const list: { y: number; draw: () => void }[] = [];
    for (const side of ['red', 'blue'] as Side[]) {
      if (side === 'red' && vm.board.defense) continue; // 防衛戦は敵本陣が無い
      const p = this.center(vm.board.cores[side].cell);
      list.push({ y: p.y, draw: () => this.drawCore(ctx, side, p) });
    }
    vm.board.tiles.forEach((t, i) => {
      if (!t.relay) return;
      const p = this.center(i);
      list.push({ y: p.y - 2, draw: () => this.drawRelay(ctx, p, vm.owners[i]) });
    });
    for (const u of vm.units.values()) {
      if (u.alpha <= 0 || u.cell == null || u.hidden) continue;
      const p = this.unitPos(u);
      list.push({ y: p.y + (u.kind === 'building' ? -1 : 0), draw: () => (u.kind === 'building' ? this.drawBuilding(ctx, u, p) : this.drawUnit(ctx, u, p, now)) });
    }
    // 灯籠陣：陣の空いているマスに光る石灯籠を立てる（駒・建物・本陣・龍脈のマスは避ける）
    const zones = vm.board.zones ?? [];
    if (zones.some((z) => z.kind === 'lantern')) {
      const busy = new Set<number>([vm.board.cores.red.cell, vm.board.cores.blue.cell]);
      for (const u of vm.units.values()) if (u.cell != null && u.alpha > 0) busy.add(u.cell);
      vm.board.tiles.forEach((t, i) => t.relay && busy.add(i));
      const glow = this.reduced ? 0.8 : 0.85 + 0.15 * Math.sin(now / 380);
      for (const z of zones) {
        if (z.kind !== 'lantern') continue;
        for (const c of z.cells) {
          if (busy.has(c)) continue;
          const p = this.center(c);
          list.push({
            y: p.y - 3,
            draw: () => {
              // 足元の暖かい灯り＋灯籠（絵はマスの中で小さいので大きめに）
              const g = ctx.createRadialGradient(p.x, p.y + s * 0.05, 0, p.x, p.y + s * 0.05, s * 0.7);
              g.addColorStop(0, `rgba(255,200,110,${0.55 * glow})`);
              g.addColorStop(1, 'rgba(255,200,110,0)');
              ctx.fillStyle = g;
              ctx.beginPath();
              ctx.ellipse(p.x, p.y + s * 0.05, s * 0.7, s * 0.4, 0, 0, Math.PI * 2);
              ctx.fill();
              drawSprite(ctx, { sheet: 'fxJutsu', col: 2, row: 0, foot: 0.1 }, p.x, p.y + s * 0.3, s * 1.45, glow);
            },
          });
        }
      }
    }
    if (this.hl.ghost) {
      const g = this.hl.ghost;
      const p = this.center(g.cell);
      list.push({ y: p.y, draw: () => this.drawUnitSprite(ctx, g.unit, p.x, p.y, 0.5) });
    }
    list.sort((a, b) => a.y - b.y);
    this.lateBars = [];
    for (const it of list) it.draw();
    this.drawRespawns(ctx);
    const pulse = this.reduced ? 0.6 : 0.5 + 0.5 * Math.sin(now / 150);
    for (const t of this.hl.targets) {
      const { x, y } = this.center(t.cell);
      ctx.beginPath();
      ctx.arc(x, y - s * 0.2, s * (t.core ? 1.05 : 0.8) + pulse * 2, 0, Math.PI * 2);
      ctx.strokeStyle = t.selected ? '#ffe07a' : `rgba(255,80,70,${0.7 + 0.3 * pulse})`;
      ctx.lineWidth = t.selected ? 4 : 3;
      ctx.stroke();
    }
    // 予測ダメージの出ている HP のゲージは、駒の絵と狙いの輪の上に描く（手前の駒に隠れないように）
    for (const f of this.lateBars) f();
    this.lateBars = [];
  }

  /** 倒れた武将の戻るマス：点線の輪と「復活 あとN」（自分の手番であと何回） */
  private drawRespawns(ctx: CanvasRenderingContext2D) {
    const list = this.vm?.board.respawns;
    if (!list?.length) return;
    const s = this.s;
    for (const r of list) {
      const { x, y } = this.center(r.cell);
      const blue = r.side === 'blue';
      ctx.save();
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = blue ? 'rgba(170,215,255,0.95)' : 'rgba(255,170,150,0.95)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(x, y - s * 0.2, s * 0.72, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
      const text = r.turns > 0 ? `復活 あと${r.turns}` : 'まもなく復活';
      ctx.font = `800 ${Math.max(10, Math.round(s * 0.36))}px "Noto Sans JP", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const w = ctx.measureText(text).width + s * 0.4;
      const h = Math.max(14, s * 0.56);
      const ty = y - s * 1.05;
      ctx.fillStyle = blue ? 'rgba(20,50,95,0.9)' : 'rgba(95,25,20,0.9)';
      ctx.beginPath();
      ctx.roundRect(x - w / 2, ty - h / 2, w, h, h / 2);
      ctx.fill();
      ctx.strokeStyle = blue ? 'rgba(170,215,255,0.9)' : 'rgba(255,180,160,0.9)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.fillStyle = '#fff';
      ctx.fillText(text, x, ty + 0.5);
      ctx.restore();
    }
  }

  private drawCore(ctx: CanvasRenderingContext2D, side: Side, p: { x: number; y: number }) {
    const vm = this.vm!;
    const s = this.s;
    const hp = vm.cores[side];
    const max = vm.board.cores[side].max;
    const col = vm.collapse[side];
    const down = hp <= 0 && col == null;
    // 敵の城の結界：番人（敵の城の手前に立つボス）が生きている間だけ張られている（本陣への攻撃が減る）。倒すと割れて、もう戻らない
    const now = performance.now();
    const want = side === 'red' && vm.board.guardian && !down && col == null && !vm.board.defense ? 1 : 0;
    const breaking = now - this.barrierAt[side] < BARRIER_BREAK_MS;
    if (!breaking) this.barrierVis[side] += (want - this.barrierVis[side]) * (want ? 0.04 : 0.2);
    // 落ちた本陣：沈み込んで黒ずみ、燃え続ける（崩れる間に火が強まる）
    const burn = col != null ? Math.min(1, col * 1.6) : down ? 1 : 0;
    const alpha = burn ? 1 - burn * 0.25 : 1;
    const sink = burn * s * 0.22;
    if (burn) ctx.filter = `brightness(${1 - burn * 0.55}) saturate(${1 - burn * 0.5})`;
    const drawn = drawSprite(ctx, side === 'blue' ? WORLD.coreBlue : WORLD.coreRed, p.x, p.y + s * 0.75 + sink, s * 2.4, alpha);
    ctx.filter = 'none';
    if (burn) this.drawBurning(ctx, p, burn);
    if (!drawn) {
      this.hexPath(ctx, p.x, p.y, s * 0.8);
      ctx.fillStyle = side === 'blue' ? '#2f6fd0' : '#c8402f';
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = `700 ${Math.round(s * 0.6)}px "Shippori Mincho B1", serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('陣', p.x, p.y);
    }
    if (this.barrierVis[side] > 0.02) this.drawBarrier(ctx, side, p, this.barrierVis[side], now);
    if (breaking) this.drawBarrierBreak(ctx, side, p, (now - this.barrierAt[side]) / BARRIER_BREAK_MS);
    // 防衛戦の敵本陣（妖の巣）は攻撃できないので HP を出さない
    if (down || burn || (side === 'red' && vm.board.defense)) return;
    const w = s * 1.9;
    const y = p.y - s * 1.6;
    this.bar(ctx, p.x - w / 2, y, w, 7, hp / max, side === 'blue' ? '#6fb0ff' : '#ff6b5f', this.previewAt(vm.board.cores[side].cell));
  }

  /** 城の結界：本陣を包む半球（縁が光り、経線がゆっくり流れ、足元に回る紋の輪）。敵の城は紫、自分の城は青 */
  private drawBarrier(ctx: CanvasRenderingContext2D, side: Side, p: { x: number; y: number }, vis: number, now: number) {
    const s = this.s;
    // 絵（fx-barrier.webp の1行目：ゆらめく4コマを重ねて送る）。無ければ図形
    const img = sheet('fxBarrier');
    if (img) {
      const f = this.reduced ? 0 : (now / 220) % 4;
      const a = Math.floor(f);
      const k = f - a;
      this.barrierCell(ctx, img, a, p, vis * (1 - k));
      this.barrierCell(ctx, img, (a + 1) % 4, p, vis * k);
      return;
    }
    const c = side === 'red' ? '200,120,255' : '120,200,255';
    const cx = p.x;
    const by = p.y + s * 0.5;
    const rx = s * 1.3;
    const ry = s * 1.75;
    const t = this.reduced ? 0 : now / 1000;
    const pulse = 0.85 + 0.15 * Math.sin(t * 2.2 + (side === 'red' ? 1 : 0));
    ctx.save();
    ctx.globalAlpha *= vis;
    ctx.globalCompositeOperation = 'lighter';
    // 半球の面：中は薄く、縁ほど濃く
    ctx.beginPath();
    ctx.ellipse(cx, by, rx, ry, 0, Math.PI, Math.PI * 2);
    ctx.ellipse(cx, by, rx, rx * 0.3, 0, 0, Math.PI, false);
    const g = ctx.createRadialGradient(cx, by - ry * 0.35, rx * 0.15, cx, by - ry * 0.2, rx * 1.15);
    g.addColorStop(0, `rgba(${c},0.02)`);
    g.addColorStop(0.7, `rgba(${c},${0.07 * pulse})`);
    g.addColorStop(1, `rgba(${c},${0.4 * pulse})`);
    ctx.fillStyle = g;
    ctx.fill();
    // 経線（ゆっくり流れる）
    ctx.lineWidth = 1;
    for (let k = 0; k < 4; k++) {
      const ph = ((k / 4 + t * 0.08) % 1) * 2 - 1;
      ctx.beginPath();
      ctx.ellipse(cx, by, Math.abs(ph) * rx, ry, 0, Math.PI, Math.PI * 2);
      ctx.strokeStyle = `rgba(${c},${0.18 * (1 - Math.abs(ph) * 0.6)})`;
      ctx.stroke();
    }
    // 縁の光
    ctx.beginPath();
    ctx.ellipse(cx, by, rx, ry, 0, Math.PI, Math.PI * 2);
    ctx.lineWidth = 2;
    ctx.strokeStyle = `rgba(${c},${0.8 * pulse})`;
    ctx.shadowColor = `rgba(${c},0.9)`;
    ctx.shadowBlur = 8;
    ctx.stroke();
    ctx.shadowBlur = 0;
    // 足元の紋の輪（回る）
    ctx.beginPath();
    ctx.ellipse(cx, by, rx, rx * 0.3, 0, 0, Math.PI * 2);
    ctx.setLineDash([s * 0.18, s * 0.12]);
    ctx.lineDashOffset = -t * s * 0.6;
    ctx.lineWidth = 2;
    ctx.strokeStyle = `rgba(${c},${0.5 * pulse})`;
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  }

  /** 結界の絵の1コマを本陣に重ねる（コマの下端＝結界の足元を本陣の足元に） */
  private barrierCell(ctx: CanvasRenderingContext2D, img: HTMLImageElement | ImageBitmap, i: number, p: { x: number; y: number }, alpha: number, scale = 1) {
    if (alpha <= 0.01) return;
    const s = this.s;
    const cw = img.width / 4;
    const ch = img.height / 2;
    const size = s * 4.05 * scale;
    const sx = (i % 4) * cw;
    const sy = Math.floor(i / 4) * ch;
    // 城の絵はコマの中で右に寄っている（城の真ん中はマスの中心より s×0.18 右）。結界も城の真ん中に合わせる（ずれると、てっぺんの飾りが丸みの低い所から飛び出す）
    const dx = p.x + s * 0.18 - size / 2;
    // 絵の下端の少し上に法陣の輪の中心があるので、輪が城の足元（石段の下）に来るよう下げる
    const dy = p.y + s * 1.36 - size;
    ctx.save();
    // 明るい盤の上でも紫が出るように、ふつうに重ねてから加算で少しだけ光らせる（加算だけだと白っぽく色が飛ぶ）
    ctx.globalAlpha *= alpha * 0.8;
    ctx.drawImage(img, sx, sy, cw, ch, dx, dy, size, size);
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha *= 0.35;
    ctx.drawImage(img, sx, sy, cw, ch, dx, dy, size, size);
    ctx.restore();
  }

  /** 結界が割れる：一瞬白く光ってひびが走り、破片が外へ飛び散って落ちる。衝撃の輪が広がる（t＝0..1） */
  private drawBarrierBreak(ctx: CanvasRenderingContext2D, side: Side, p: { x: number; y: number }, t: number) {
    const s = this.s;
    // 絵（2行目：ひび→割れる→砕け散る→消える の4コマを重ねて送る）。最後は薄れて消える
    const img = sheet('fxBarrier');
    if (img) {
      const f = Math.min(3.999, t * 4);
      const a = Math.floor(f);
      const k = f - a;
      const fade = t > 0.8 ? (1 - t) / 0.2 : 1;
      this.barrierCell(ctx, img, 4 + a, p, fade * (1 - k), 1 + t * 0.15);
      if (a < 3) this.barrierCell(ctx, img, 4 + a + 1, p, fade * k, 1 + t * 0.15);
      return;
    }
    const c = side === 'red' ? '220,150,255' : '150,215,255';
    const cx = p.x;
    const by = p.y + s * 0.5;
    const rx = s * 1.3;
    const ry = s * 1.75;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    // はじめの一瞬：結界が白く光ってひびが走る
    if (t < 0.25) {
      const k = 1 - t / 0.25;
      ctx.beginPath();
      ctx.ellipse(cx, by, rx, ry, 0, Math.PI, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${0.35 * k})`;
      ctx.fill();
      ctx.strokeStyle = `rgba(255,255,255,${0.9 * k})`;
      ctx.lineWidth = 2;
      for (let i = 0; i < 7; i++) {
        const a = Math.PI + (i + 0.5) * (Math.PI / 7);
        let x = cx;
        let y = by - ry * 0.55;
        ctx.beginPath();
        ctx.moveTo(x, y);
        for (let j = 1; j <= 3; j++) {
          const r = j / 3;
          x = cx + Math.cos(a) * rx * r + Math.sin(i * 7 + j) * s * 0.08;
          y = by - ry * 0.55 + Math.sin(a) * ry * 0.45 * r + Math.cos(i * 3 + j) * s * 0.06;
          ctx.lineTo(x, y);
        }
        ctx.stroke();
      }
    }
    // 衝撃の輪
    const rr = 1 + t * 1.2;
    ctx.beginPath();
    ctx.ellipse(cx, by, rx * rr, rx * 0.3 * rr, 0, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(${c},${0.8 * (1 - t)})`;
    ctx.lineWidth = 3 * (1 - t) + 1;
    ctx.stroke();
    // 破片（結界の面から外へ、回りながら落ちる）
    const n = 18;
    for (let i = 0; i < n; i++) {
      const a = Math.PI + (i + 0.5) * (Math.PI / n);
      const sx = cx + Math.cos(a) * rx * 0.95;
      const sy = by + Math.sin(a) * ry * 0.95;
      const sp = s * (1.4 + ((i * 37) % 10) / 10);
      const x = sx + Math.cos(a) * sp * t;
      const y = sy + Math.sin(a) * sp * t * 0.6 + s * 2.2 * t * t;
      const sz = s * (0.1 + ((i * 13) % 7) / 50);
      const rot = t * (6 + (i % 5)) + i;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.beginPath();
      ctx.moveTo(0, -sz);
      ctx.lineTo(sz * 0.8, sz * 0.6);
      ctx.lineTo(-sz * 0.7, sz * 0.5);
      ctx.closePath();
      ctx.fillStyle = `rgba(${c},${0.85 * (1 - t)})`;
      ctx.fill();
      ctx.restore();
    }
    ctx.restore();
  }

  /** 燃える本陣：炎上用の絵（fx2.webp。大・中・小の炎の揺らめき各4コマ、火の粉など）を加算合成で重ね、
   *  後ろに柔らかい黒煙を立ちのぼらせる。絵が無ければ図形の炎（drawBurningShapes） */
  private drawBurning(ctx: CanvasRenderingContext2D, p: { x: number; y: number }, f: number) {
    const img = sheet('fx2');
    if (!img) return this.drawBurningShapes(ctx, p, f);
    const s = this.s;
    const now = performance.now();
    const calm = this.reduced ? 0 : 1;
    const cw = img.width / 4;
    const ch = img.height / 4;
    const cell = (i: number, cx: number, base: number, size: number, alpha: number, flip = false) => {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.translate(cx, base);
      if (flip) ctx.scale(-1, 1);
      ctx.drawImage(img, (i % 4) * cw, Math.floor(i / 4) * ch, cw, ch, -size / 2, -size * 0.95, size, size);
      ctx.restore();
    };
    // 黒煙（炎の後ろ）：柔らかい塊が重なって、ふくらみながら上へ流れる
    for (let i = 0; i < 7; i++) {
      const ph = ((now / 2600) * (calm || 0.0001) + i / 7) % 1;
      const x = p.x + Math.sin(i * 1.9 + ph * 2.4) * s * 0.55 + ph * s * 0.5;
      const y = p.y - s * 1.1 - ph * s * 2.6;
      const r = s * (0.5 + ph * 1.1);
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      const a = 0.5 * (1 - ph) * f;
      g.addColorStop(0, `rgba(28,24,26,${a.toFixed(3)})`);
      g.addColorStop(0.6, `rgba(40,34,36,${(a * 0.55).toFixed(3)})`);
      g.addColorStop(1, 'rgba(40,34,36,0)');
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    // 足元の照り返し
    const glow = ctx.createRadialGradient(p.x, p.y + s * 0.25, s * 0.1, p.x, p.y + s * 0.25, s * 1.7);
    glow.addColorStop(0, `rgba(255,140,40,${(0.6 * f).toFixed(3)})`);
    glow.addColorStop(1, 'rgba(255,120,30,0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y + s * 0.25, s * 1.7, s * 0.9, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const frame = (row: number, phase: number, fps = 11) => row * 4 + (calm ? Math.floor(now / (1000 / fps) + phase) % 4 : phase % 4);
    const pulse = (k: number) => 1 + calm * 0.06 * Math.sin(now / 130 + k * 1.7);
    // 根元の強い光
    cell(14, p.x, p.y + s * 0.55, s * 2.2 * pulse(0), 0.6 * f);
    // 奥：大きな炎3本
    [-0.55, 0.05, 0.6].forEach((dx, k) => cell(frame(0, k * 1.3), p.x + dx * s, p.y + s * 0.3, s * (k === 1 ? 2.2 : 1.8) * pulse(k) * (0.6 + 0.4 * f), 0.8 * f, k === 2));
    // 手前：中くらいの炎2本
    [-0.85, 0.85].forEach((dx, k) => cell(frame(1, k * 2 + 1), p.x + dx * s, p.y + s * 0.55, s * 1.5 * pulse(k + 3) * (0.6 + 0.4 * f), 0.9 * f, k === 1));
    // 根元の小さな炎
    [-0.3, 0.35].forEach((dx, k) => cell(frame(2, k * 3 + 2, 13), p.x + dx * s, p.y + s * 0.7, s * 1.0 * pulse(k + 5), 0.85 * f, k === 0));
    // 火の粉の群れ・筋（ゆっくり上へ）
    if (calm) {
      const ph = (now / 1800) % 1;
      cell(12, p.x + s * 0.2, p.y - s * 0.6 - ph * s * 1.2, s * 1.8, 0.8 * f * (1 - ph));
      const ph2 = (now / 2300 + 0.5) % 1;
      cell(15, p.x - s * 0.35, p.y - s * 0.4 - ph2 * s * 1.4, s * 1.6, 0.7 * f * (1 - ph2), true);
    }
    ctx.restore();
  }

  /** 燃える本陣（絵が無いとき）：図形の炎・黒煙・火の粉 */
  private drawBurningShapes(ctx: CanvasRenderingContext2D, p: { x: number; y: number }, f: number) {
    const s = this.s;
    const now = performance.now();
    const calm = this.reduced ? 0 : 1;
    // 黒煙（後ろから上へ流れて薄れる）
    for (let i = 0; i < 5; i++) {
      const ph = (now / 1900 + i / 5) % 1;
      const x = p.x + Math.sin(i * 2.3 + ph * 2) * s * 0.5 + ph * s * 0.4;
      const y = p.y - s * 0.6 - ph * s * 2.2;
      ctx.fillStyle = `rgba(35,30,30,${(0.42 * (1 - ph) * f).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(x, y, s * (0.3 + ph * 0.75), 0, Math.PI * 2);
      ctx.fill();
    }
    // 足元の火の照り返し
    const glow = ctx.createRadialGradient(p.x, p.y + s * 0.2, s * 0.1, p.x, p.y + s * 0.2, s * 1.4);
    glow.addColorStop(0, `rgba(255,120,30,${(0.55 * f).toFixed(3)})`);
    glow.addColorStop(1, 'rgba(255,120,30,0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y + s * 0.2, s * 1.4, s * 0.8, 0, 0, Math.PI * 2);
    ctx.fill();
    // 炎（大小5つ、ちらちら揺れる）
    const flames = [-0.75, -0.38, 0, 0.4, 0.78];
    flames.forEach((dx, i) => {
      const flick = calm * Math.sin(now / 85 + i * 1.7) * 0.14;
      const h = s * 1.3 * (0.55 + (i === 2 ? 0.5 : 0.15 * ((i * 7) % 3))) * (0.6 + 0.4 * f) * (1 + flick);
      this.flame(ctx, p.x + dx * s, p.y + s * 0.35 - Math.abs(dx) * s * 0.15, h, 'rgba(255,110,30,0.92)', '#ffe08a', f, calm * Math.sin(now / 140 + i) * 0.18);
    });
    // 火の粉
    if (calm)
      for (let i = 0; i < 7; i++) {
        const ph = (now / 1300 + i / 7) % 1;
        const x = p.x + Math.sin(i * 3.1 + ph * 6) * s * 0.7;
        const y = p.y - ph * s * 2;
        ctx.fillStyle = `rgba(255,${180 + ((i * 37) % 60)},90,${(0.9 * (1 - ph) * f).toFixed(3)})`;
        ctx.fillRect(x - 1.5, y - 1.5, 3, 3);
      }
  }

  private drawRelay(ctx: CanvasRenderingContext2D, p: { x: number; y: number }, owner: Side | null) {
    const s = this.s;
    // 持ち主：足元の光の輪（陣営の色）。中立は無し
    if (owner) {
      const g = ctx.createRadialGradient(p.x, p.y + s * 0.35, s * 0.1, p.x, p.y + s * 0.35, s * 0.85);
      g.addColorStop(0, `${SIDE_COLOR[owner]}cc`);
      g.addColorStop(1, `${SIDE_COLOR[owner]}00`);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(p.x, p.y + s * 0.35, s * 0.85, s * 0.42, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    // 龍脈：持ち主の色の妖気（中立は翡翠）。ゆらゆら揺らぐ（動きを減らす設定なら止める）
    // 龍脈：注連縄の大岩の裂け目から妖気（静止画）。妖気の色は持ち主（中立は翡翠・自陣は青・敵陣は赤）
    const ref = owner ? firstLoaded([{ sheet: `relay_${owner}`, col: 0, row: 0, foot: 0.03 }]) ?? relaySprite() : relaySprite();
    const drawn = drawSprite(ctx, ref, p.x, p.y + s * 0.55, s * 1.9);
    if (!drawn) {
      ctx.fillStyle = '#e8a33a';
      ctx.fillRect(p.x - s * 0.3, p.y - s * 1, s * 0.6, s * 1.1);
    }
    // 持ち主は妖気の色で分かるので旗は立てない
  }

  /** そのマスの相手への予測ダメージ（攻撃・術を構えている間）。HP のゲージの中で、減る分を点滅させる */
  /** 賽の役・賽の効果がある手番：足元から金の光が立ち、体の縁が光る（貴重な役は虹色に強く） */
  private drawLuck(ctx: CanvasRenderingContext2D, u: VMUnit, p: { x: number; y: number }, now: number) {
    const s = this.s;
    const rare = u.luck?.role === 'pinzoro' || u.luck?.role === 'zorome';
    const t = this.reduced ? 0.6 : 0.5 + 0.5 * Math.sin(now / (rare ? 120 : 220));
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const g = ctx.createRadialGradient(p.x, p.y - s * 0.3, s * 0.1, p.x, p.y - s * 0.3, s * (rare ? 1.25 : 0.95));
    const hue = rare ? (now / 8) % 360 : 42;
    g.addColorStop(0, `hsla(${hue}, 100%, 70%, ${0.35 + 0.3 * t})`);
    g.addColorStop(1, `hsla(${hue}, 100%, 55%, 0)`);
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y - s * 0.3, s * (rare ? 1.25 : 0.95), s * (rare ? 1.35 : 1.05), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  private lateBars: (() => void)[] = [];
  private previewAt(cell: number | null | undefined) {
    return cell == null ? undefined : this.hl.previews.find((f) => f.cell === cell);
  }
  private bar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, frac: number, color: string, pv?: { hp: number; after: number; max: number; selected: boolean }) {
    if (pv) {
      const before = Math.max(0, Math.min(1, pv.hp / pv.max));
      const after = Math.max(0, Math.min(before, pv.after / pv.max));
      // 下地と、外側だけ丸い枠の中に、残る分と減る分をまっすぐな切れ目で描く
      ctx.fillStyle = 'rgba(6,10,24,0.85)';
      ctx.beginPath();
      ctx.roundRect(x - 1.5, y - 1.5, w + 3, h + 3, (h + 3) / 2);
      ctx.fill();
      ctx.save();
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, h / 2);
      ctx.clip();
      ctx.fillStyle = color;
      ctx.fillRect(x, y, w * after, h);
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.fillRect(x, y, w * after, Math.max(1, h * 0.35));
      // 減る分：白と朱で点滅（選んでいる相手ははっきり、範囲で巻き込む相手は控えめ）
      const blink = 0.5 + 0.5 * Math.sin(performance.now() / 140);
      ctx.fillStyle = pv.selected ? `rgba(255, ${Math.round(150 + 105 * blink)}, ${Math.round(120 + 135 * blink)}, 0.95)` : `rgba(255, 190, 140, ${0.45 + 0.35 * blink})`;
      ctx.fillRect(x + w * after, y, w * (before - after), h);
      ctx.restore();
      if (after <= 0) {
        ctx.strokeStyle = `rgba(255, 230, 140, ${0.6 + 0.4 * blink})`;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.roundRect(x - 2.5, y - 2.5, w + 5, h + 5, (h + 5) / 2);
        ctx.stroke();
      }
      return;
    }
    ctx.fillStyle = 'rgba(6,10,24,0.85)';
    ctx.beginPath();
    ctx.roundRect(x - 1.5, y - 1.5, w + 3, h + 3, (h + 3) / 2);
    ctx.fill();
    const f = Math.max(0, Math.min(1, frac));
    if (f > 0) {
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.roundRect(x, y, Math.max(h, f * w), h, h / 2);
      ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.fillRect(x + h / 2, y, Math.max(0, f * w - h), Math.max(1, h * 0.35));
    }
  }

  /** 鳥居の結界（そばの1マス＝状態異常にかからない・治る）を点線のヘックスで示す */
  private drawWards(ctx: CanvasRenderingContext2D) {
    const s = this.s;
    ctx.save();
    ctx.setLineDash([3, 3]);
    ctx.lineWidth = 1.5;
    for (const u of this.vm!.units.values()) {
      if (u.kind !== 'building' || u.btype !== 'torii' || !u.alive || u.cell == null) continue;
      ctx.strokeStyle = u.side === 'blue' ? 'rgba(255,226,140,0.75)' : 'rgba(255,170,150,0.6)';
      for (const c of [u.cell, ...CELLS[u.cell].n]) {
        const p = this.center(c);
        this.hexPath(ctx, p.x, p.y, s * 0.8);
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  /** 敵の型の印（左肩）：重装は鋼の盾に鎧の残り、攻城は狙う本陣の色の城の札 */
  private drawEnemyType(ctx: CanvasRenderingContext2D, u: VMUnit, p: { x: number; y: number }) {
    const t = u.etype;
    if (!t) return;
    if (t.type === 'heavy' && !(t.armor && t.armor > 0)) return;
    const s = this.s;
    const r = Math.max(8, s * 0.27);
    const x = p.x - s * 0.62;
    const y = p.y - s * 0.55;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.beginPath();
    if (t.type === 'heavy') {
      // 盾形
      ctx.moveTo(x - r, y - r * 0.9);
      ctx.lineTo(x + r, y - r * 0.9);
      ctx.lineTo(x + r, y + r * 0.1);
      ctx.quadraticCurveTo(x + r, y + r * 0.9, x, y + r * 1.15);
      ctx.quadraticCurveTo(x - r, y + r * 0.9, x - r, y + r * 0.1);
      ctx.closePath();
      ctx.fillStyle = '#5b6b82';
    } else {
      ctx.roundRect(x - r, y - r, r * 2, r * 2, r * 0.35);
      ctx.fillStyle = t.siege === 'blue' ? '#c0392b' : '#8a5a2b';
    }
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = '#fff8e8';
    ctx.stroke();
    ctx.font = `900 ${Math.round(r * 1.1)}px "Noto Sans JP", sans-serif`;
    ctx.fillStyle = '#fff';
    ctx.fillText(t.type === 'heavy' ? '鎧' : '攻', x, y + 0.5);
    if (t.type === 'heavy' && (t.armorMax ?? 1) > 1) {
      ctx.font = `700 ${Math.max(8, Math.round(r * 0.85))}px "Noto Sans JP", sans-serif`;
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.strokeText(String(t.armor), x + r * 0.85, y + r * 0.8);
      ctx.fillText(String(t.armor), x + r * 0.85, y + r * 0.8);
    }
    ctx.restore();
  }

  /** 状態異常の印（頭の上に丸い札：字＋残り手番） */
  private drawStatus(ctx: CanvasRenderingContext2D, u: VMUnit, p: { x: number; y: number }) {
    const list = u.status;
    if (!list?.length) return;
    const s = this.s;
    const r = Math.max(8, s * 0.26);
    const y = p.y - s * (u.kind === 'boss' ? 1.45 : 1.05);
    let x = p.x - ((list.length - 1) * r * 2.2) / 2;
    ctx.save();
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    for (const st of list) {
      const m = STATUS_MARK[st.key];
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = m.bg;
      ctx.fill();
      // 良い効果は金の縁（色だけでなく縁の太さでも見分ける）
      ctx.lineWidth = m.good ? 2.5 : 1.5;
      ctx.strokeStyle = m.good ? '#ffd27a' : '#fff8e8';
      ctx.stroke();
      ctx.font = `900 ${Math.round(r * 1.15)}px "Noto Sans JP", sans-serif`;
      ctx.fillStyle = '#fff';
      ctx.fillText(m.ch, x, y + 0.5);
      ctx.font = `700 ${Math.max(8, Math.round(r * 0.85))}px "Noto Sans JP", sans-serif`;
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.strokeText(String(st.turns), x + r * 0.85, y + r * 0.7);
      ctx.fillText(String(st.turns), x + r * 0.85, y + r * 0.7);
      x += r * 2.2;
    }
    ctx.restore();
  }

  private drawBuilding(ctx: CanvasRenderingContext2D, u: VMUnit, p: { x: number; y: number }) {
    const s = this.s;
    const info = u.btype ? BUILDING_INFO[u.btype] : null;
    ctx.save();
    ctx.globalAlpha = u.alpha * (u.btype === 'otoshiana' ? 0.75 : 1);
    ctx.fillStyle = u.side === 'blue' ? 'rgba(70,150,255,0.55)' : 'rgba(255,80,60,0.55)';
    ctx.beginPath();
    ctx.ellipse(p.x, p.y + s * 0.38, s * 0.62, s * 0.24, 0, 0, Math.PI * 2);
    ctx.fill();
    if (u.flash > 0) {
      ctx.filter = `brightness(${1 + u.flash * 2})`;
      u.flash = Math.max(0, u.flash - 0.07);
    }
    // 敵の建物は赤黒の版（無ければ赤を重ねる）で、味方の建物と見分けやすく
    const drawn =
      u.btype &&
      (u.side === 'blue'
        ? drawSprite(ctx, buildingSprite(u.btype), p.x, p.y + s * 0.55, s * 1.7)
        : drawSprite(ctx, enemyBuildingSprite(u.btype), p.x, p.y + s * 0.55, s * 1.7) || drawSpriteTinted(ctx, buildingSprite(u.btype), p.x, p.y + s * 0.55, s * 1.7, '#e0281e', 0.45));
    if (!drawn) {
      ctx.fillStyle = info?.color ?? '#8a6f4e';
      ctx.fillRect(p.x - s * 0.36, p.y - s * 0.62, s * 0.72, s * 0.9);
      ctx.strokeStyle = '#1a1208';
      ctx.lineWidth = 2;
      ctx.strokeRect(p.x - s * 0.36, p.y - s * 0.62, s * 0.72, s * 0.9);
      ctx.fillStyle = '#fff8e8';
      ctx.font = `700 ${Math.round(s * 0.5)}px "Shippori Mincho B1", serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(info?.short ?? '建', p.x, p.y - s * 0.17);
    }
    ctx.filter = 'none';
    const pv = this.previewAt(u.cell);
    if (pv) this.lateBars.push(() => this.bar(ctx, p.x - s * 0.6, p.y + s * 0.6, s * 1.2, 6, u.hp / u.max, u.side === 'blue' ? '#6fb0ff' : '#ff6b5f', pv));
    else if (u.btype !== 'otoshiana') this.bar(ctx, p.x - s * 0.5, p.y + s * 0.6, s, 3, u.hp / u.max, u.side === 'blue' ? '#6fb0ff' : '#ff6b5f');
    ctx.restore();
  }

  private drawUnitSprite(ctx: CanvasRenderingContext2D, u: VUnit, x: number, y: number, alpha: number) {
    const s = this.s;
    const big = u.kind === 'boss';
    // 味方（自分のヒーロー・お供）は2割大きく描いて見つけやすく
    const size = (big ? s * 2.9 : s * 2.0) * (u.side === 'blue' ? 1.2 : 1);
    const bottom = y + s * (big ? 0.7 : 0.5);
    if (u.kind === 'hero' && u.charId) {
      // 妖力 参は覚醒の姿（読めなければ通常の絵。オーラは drawUnit が強めに描く）
      if ((u.surge ?? 1) >= 3 && drawSprite(ctx, AWAKE_SPRITES[u.charId], x, bottom, size * 1.22, alpha)) return;
      if (drawSprite(ctx, HERO_SPRITES[u.charId], x, bottom, size * 1.15, alpha)) return;
    } else if (u.enemyId) {
      // 敵の大将は味方のヒーローと同じ形式・同じ大きさのちびキャラ（leaders.webp）。無ければ敵の絵
      if (u.kind === 'rival' && drawSprite(ctx, leaderSprite(u.enemyId), x, bottom, size * 1.2 * 1.15, alpha)) return;
      if (drawSprite(ctx, enemySprite(u.enemyId), x, bottom, size, alpha)) return;
    }
    if (u.kind === 'hero') {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = '#3d7bd8';
      ctx.beginPath();
      ctx.arc(x, y - s * 0.2, s * 0.55, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      return;
    }
    drawEnemyPlaceholder(ctx, u.name, u.role ?? 'small', x, y - s * (big ? 0.35 : 0.2), s * (big ? 0.8 : 0.55), alpha);
  }

  private drawUnit(ctx: CanvasRenderingContext2D, u: VMUnit, p: { x: number; y: number }, now: number) {
    const s = this.s;
    const big = u.kind === 'boss';
    ctx.save();
    ctx.globalAlpha = u.alpha;
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.beginPath();
    ctx.ellipse(p.x, p.y + s * 0.42, s * (big ? 1.0 : 0.6), s * (big ? 0.32 : 0.22), 0, 0, Math.PI * 2);
    ctx.fill();
    const inside = !!u.inside && u.px == null;
    if (inside) this.drawInside(ctx, u, p, now, true);
    else if (!u.inside) this.insideAt.delete(u.id);
    if (u.kind === 'hero' || u.kind === 'rival') {
      ctx.strokeStyle = SIDE_COLOR[u.side];
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.ellipse(p.x, p.y + s * 0.42, s * 0.62, s * 0.24, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    const bob = (u.kind === 'hero' || u.kind === 'rival') && !this.reduced ? Math.sin(now / 260 + u.id) * 1.2 : 0;
    const surge = u.surge ?? 1;
    if (surge >= 2) this.drawAura(ctx, u, p, now, surge, true);
    if (u.flash > 0) {
      ctx.filter = `brightness(${1 + u.flash * 2.5})`;
      u.flash = Math.max(0, u.flash - 0.07);
    }
    this.drawUnitSprite(ctx, u, p.x, p.y + bob, 1);
    ctx.filter = 'none';
    if (surge >= 2) this.drawAura(ctx, u, p, now, surge, false);
    if (inside) this.drawInside(ctx, u, p, now, false);
    if (u.luck) this.drawLuck(ctx, u, p, now);
    if (u.defending) {
      ctx.strokeStyle = 'rgba(140,200,255,0.9)';
      ctx.lineWidth = 2;
      this.hexPath(ctx, p.x, p.y - s * 0.15, s * 0.75);
      ctx.stroke();
    }
    const w = big ? s * 1.6 : s * 1.05;
    const by = p.y + s * (big ? 0.74 : 0.56);
    const bh = Math.max(4, Math.round(s * (big ? 0.16 : 0.12)));
    // 陣営は HP バーの色で分かる（左の印は置かない）
    const pv = this.previewAt(u.cell);
    if (pv) {
      const W = w * 1.25;
      const H = Math.round(bh * 1.6);
      this.lateBars.push(() => this.bar(ctx, p.x - W / 2, by, W, H, u.hp / u.max, HP_COLOR[u.side], pv));
    } else this.bar(ctx, p.x - w / 2, by, w, bh, u.hp / u.max, HP_COLOR[u.side]);
    if (u.lv && (u.kind === 'hero' || u.kind === 'rival')) {
      ctx.font = `700 ${Math.max(9, Math.round(s * 0.34))}px "Noto Sans JP", sans-serif`;
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = u.side === 'blue' ? '#cfe4ff' : '#ffd0c8';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      const lv = `Lv${u.lv}`;
      ctx.strokeText(lv, p.x - w / 2 - 2, by + 2);
      ctx.fillText(lv, p.x - w / 2 - 2, by + 2);
    }
    this.drawStatus(ctx, u, p);
    this.drawEnemyType(ctx, u, p);
    if (u.engaged && u.kind !== 'hero') {
      ctx.font = `900 ${Math.round(s * 0.5)}px "Noto Sans JP", sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillStyle = '#ff5a50';
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 3;
      ctx.strokeText('!', p.x + s * 0.55, p.y - s * 0.9);
      ctx.fillText('!', p.x + s * 0.55, p.y - s * 0.9);
    }
    ctx.restore();
  }

  /** 炎を1つ描く（x,y は底の位置、h は高さ） */
  private flame(ctx: CanvasRenderingContext2D, x: number, y: number, h: number, outer: string, core: string | null, alpha = 1, sway = 0) {
    ctx.save();
    ctx.globalAlpha *= alpha;
    ctx.translate(x, y);
    if (sway) ctx.transform(1, 0, sway, 1, 0, 0);
    ctx.scale(h, h);
    ctx.fillStyle = outer;
    ctx.fill(FLAME);
    if (core) {
      ctx.fillStyle = core;
      ctx.fill(FLAME_CORE);
    }
    ctx.restore();
  }

  /** 妖力のゲージ：小さな炎3つ（灯った数＝段階。次の段階までの進み具合は次の炎を下から薄く満たす）。色だけでなく数で分かる */
  private glow(stage: number, w: number, h: number) {
    const key = `${stage}:${Math.round(w)}x${Math.round(h)}`;
    let cv = this.glowCache.get(key);
    if (!cv) {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv = document.createElement('canvas');
      cv.width = Math.ceil(w * dpr);
      cv.height = Math.ceil(h * dpr);
      const g = cv.getContext('2d')!;
      g.scale(dpr, dpr);
      const [c0, c1, c2] = (SURGE_FIRE[stage] ?? SURGE_FIRE[2]).glow;
      g.translate(w / 2, h / 2);
      g.scale(1, h / w);
      // 黒紫のもや：中心は暗く、縁だけ妖しい色がにじむ
      const gr = g.createRadialGradient(0, 0, 0, 0, 0, w / 2);
      gr.addColorStop(0, c0);
      gr.addColorStop(0.5, c0);
      gr.addColorStop(0.78, c1);
      gr.addColorStop(1, c2);
      g.fillStyle = gr;
      g.beginPath();
      g.arc(0, 0, w / 2, 0, Math.PI * 2);
      g.fill();
      this.glowCache.set(key, cv);
    }
    return cv;
  }

  /** 妖力のオーラ。back＝絵の後ろ（光と大きな炎）、front＝絵の前（立ち上る小さな火の粉）。
   *  弐＝青白い妖火の揺らめき、参＝金と朱の炎（覚醒の姿が無いときは更に強く） */
  private drawAura(ctx: CanvasRenderingContext2D, u: VMUnit, p: { x: number; y: number }, now: number, stage: number, back: boolean) {
    const s = this.s;
    const still = this.reduced;
    const t = still ? 0 : now;
    const strong = stage >= 3 && !(u.kind === 'hero' && u.charId && this.awakeOk());
    const fire = SURGE_FIRE[stage] ?? SURGE_FIRE[2];
    const cy = p.y - s * 0.35;
    if (back) {
      const w = s * (stage >= 3 ? 2.7 : 2.4) * (strong ? 1.15 : 1);
      const h = w * 1.25;
      const fl = still ? 1 : 0.85 + 0.15 * Math.sin(t / 95 + u.id * 1.7) + 0.06 * Math.sin(t / 37 + u.id);
      ctx.save();
      ctx.globalAlpha *= Math.min(1, fl * (strong ? 1 : 0.9));
      // 妖気のもやは加算にしない（光ではなく、暗く纏う）
      ctx.drawImage(this.glow(stage, w, h), p.x - w / 2, cy - h / 2 - s * 0.1, w, h);
      ctx.restore();
      // 足元の炎の輪（参）・妖火の揺らめき（弐）
      const n = stage >= 3 ? (strong ? 7 : 5) : 4;
      for (let k = 0; k < n; k++) {
        const a = (k / n) * Math.PI * 2 + (still ? 0 : t / 1400);
        const rx = Math.cos(a) * s * 0.62;
        const ry = Math.sin(a) * s * 0.2;
        const flick = still ? 1 : 0.75 + 0.25 * Math.sin(t / 70 + k * 2.3 + u.id);
        const hgt = s * (stage >= 3 ? 0.62 : 0.5) * flick * (strong ? 1.2 : 1);
        this.flame(ctx, p.x + rx, p.y + s * 0.36 + ry, hgt, k % 2 ? fire.alt : fire.outer, fire.core, stage >= 3 ? 0.9 : 0.85, still ? 0 : Math.sin(t / 160 + k) * 0.25);
      }
      return;
    }
    if (still) return;
    // 立ち上る妖気（黒紫の煙がゆらいで昇る）と、まとわりつく鬼火
    const m = stage >= 3 ? 5 : 3;
    ctx.save();
    for (let k = 0; k < m; k++) {
      const ph = (t / (stage >= 3 ? 1300 : 1700) + k / m + u.id * 0.13) % 1;
      const x = p.x + Math.sin(k * 2.1 + t / 520) * s * 0.45 + Math.sin(ph * 6 + k) * s * 0.08;
      const y = p.y + s * 0.2 - ph * s * 1.9;
      const r = s * (0.16 + ph * 0.26);
      const a = Math.sin(ph * Math.PI) * (stage >= 3 ? 0.55 : 0.4);
      const gr = ctx.createRadialGradient(x, y, 0, x, y, r);
      gr.addColorStop(0, stage >= 3 ? `rgba(40,0,30,${a})` : `rgba(28,10,60,${a})`);
      gr.addColorStop(0.7, stage >= 3 ? `rgba(120,10,70,${a * 0.45})` : `rgba(80,50,190,${a * 0.4})`);
      gr.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = gr;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalCompositeOperation = 'lighter';
    const n = stage >= 3 ? 3 : 2;
    for (let k = 0; k < n; k++) {
      // 体のまわりを漂う鬼火（ゆっくり周回して上下に揺れる）
      const a = t / 900 + (k / n) * Math.PI * 2 + u.id;
      const x = p.x + Math.cos(a) * s * 0.62;
      const y = p.y - s * 0.35 + Math.sin(a * 1.7) * s * 0.18 - (Math.sin(a) > 0 ? 0 : s * 0.1);
      this.flame(ctx, x, y, s * (stage >= 3 ? 0.3 : 0.26), k % 2 ? fire.alt : fire.outer, fire.core, 0.85, Math.sin(t / 200 + k) * 0.3);
    }
    ctx.restore();
  }
  /** 自陣の中：足元に陣営色の光の輪と、立ちのぼる光の粒。入った瞬間は波紋が広がる。back＝絵の後ろ、front＝絵の前 */
  private drawInside(ctx: CanvasRenderingContext2D, u: VMUnit, p: { x: number; y: number }, now: number, back: boolean) {
    const s = this.s;
    const rgb = TERR_GLOW[u.side];
    const fy = p.y + s * 0.42;
    if (back) {
      let at = this.insideAt.get(u.id);
      if (at == null) this.insideAt.set(u.id, (at = now));
      const breathe = this.reduced ? 0.8 : 0.7 + 0.3 * Math.sin(now / 420 + u.id);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const gr = ctx.createRadialGradient(p.x, fy, 0, p.x, fy, s * 0.8);
      gr.addColorStop(0, `rgba(${rgb},${0.45 * breathe})`);
      gr.addColorStop(0.6, `rgba(${rgb},${0.2 * breathe})`);
      gr.addColorStop(1, `rgba(${rgb},0)`);
      ctx.fillStyle = gr;
      ctx.beginPath();
      ctx.ellipse(p.x, fy, s * 0.8, s * 0.32, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = `rgba(${rgb},${0.55 + 0.35 * breathe})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.ellipse(p.x, fy, s * 0.74, s * 0.29, 0, 0, Math.PI * 2);
      ctx.stroke();
      // 入った瞬間の波紋（0.7秒）
      const t = (now - at) / 700;
      if (t < 1 && !this.reduced) {
        const k = 1 - (1 - t) * (1 - t);
        ctx.strokeStyle = `rgba(${rgb},${0.9 * (1 - t)})`;
        ctx.lineWidth = 3 * (1 - t) + 1;
        ctx.beginPath();
        ctx.ellipse(p.x, fy, s * (0.6 + 0.9 * k), s * (0.23 + 0.35 * k), 0, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.restore();
      return;
    }
    if (this.reduced) return;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let k = 0; k < 4; k++) {
      const ph = (now / 1600 + k / 4 + u.id * 0.21) % 1;
      const x = p.x + Math.sin(k * 1.9 + u.id) * s * 0.5;
      const y = fy - ph * s * 1.1;
      const r = s * 0.07 * (1 - ph * 0.5);
      ctx.fillStyle = `rgba(${rgb},${Math.sin(ph * Math.PI) * 0.85})`;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
  private awakeOk() {
    return !!sheet('heroes_awake');
  }

  private drawEffects(ctx: CanvasRenderingContext2D, now: number) {
    const fxImg = sheet('fx');
    if (this.dim) {
      // 奥義：盤を暗く（すぐ暗くなり、終わりにかけて戻る）
      const t = (now - this.dim.t0) / this.dim.ms;
      if (t >= 1) this.dim = null;
      else {
        ctx.save();
        ctx.globalAlpha = 0.62 * Math.min(1, t * 8) * (t > 0.75 ? (1 - t) / 0.25 : 1);
        ctx.fillStyle = '#0a0418';
        ctx.fillRect(-this.w, -this.h, this.w * 3, this.h * 3);
        ctx.restore();
      }
    }
    if (this.parts.length) this.parts = drawParticles(ctx, fxImg ?? null, this.parts, now, (key) => sheet(key) ?? null);
    this.effects = this.effects.filter((e) => now - e.start < e.ms);
    for (const e of this.effects) {
      const t = (now - e.start) / e.ms;
      const p = this.center(e.cell);
      const f = this.center(e.from);
      const s = this.s;
      ctx.save();
      ctx.globalAlpha = 1 - t;
      ctx.strokeStyle = e.color;
      ctx.fillStyle = e.color;
      if (e.kind === 'shot' || (e.kind === 'skill' && e.from !== e.cell)) {
        const k = Math.min(1, t * 2);
        const x = f.x + (p.x - f.x) * k;
        const y = f.y + (p.y - f.y) * k - Math.sin(k * Math.PI) * s * 0.6;
        ctx.beginPath();
        ctx.arc(x, y - s * 0.3, s * (e.kind === 'skill' ? 0.3 : 0.18), 0, Math.PI * 2);
        ctx.fill();
        if (k >= 1) {
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.arc(p.x, p.y - s * 0.2, s * (0.3 + t), 0, Math.PI * 2);
          ctx.stroke();
        }
      } else if (e.kind === 'surge') {
        // 妖力の段階上昇：黒紫の妖気が噴き出し、足元から二重の輪と立ち上る鬼火
        const fire = Object.values(SURGE_FIRE).find((f) => f.outer === e.color) ?? SURGE_FIRE[2];
        const br = s * (0.6 + t * 1.6);
        const burst = ctx.createRadialGradient(p.x, p.y - s * 0.2, 0, p.x, p.y - s * 0.2, br);
        burst.addColorStop(0, `rgba(20,0,30,${0.7 * (1 - t)})`);
        burst.addColorStop(0.7, fire.glow[1].replace(/[\d.]+\)$/, `${0.5 * (1 - t)})`));
        burst.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.globalAlpha = 1;
        ctx.fillStyle = burst;
        ctx.beginPath();
        ctx.arc(p.x, p.y - s * 0.2, br, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1 - t * 0.8;
        ctx.lineWidth = 5 * (1 - t) + 1;
        for (const k of [0, 0.25]) {
          const tt = Math.max(0, t - k);
          ctx.beginPath();
          ctx.ellipse(p.x, p.y + s * 0.3, s * (0.4 + tt * 1.6), s * (0.15 + tt * 0.6), 0, 0, Math.PI * 2);
          ctx.stroke();
        }
        for (let k = 0; k < 8; k++) {
          const a = (k / 8) * Math.PI * 2;
          this.flame(ctx, p.x + Math.cos(a) * s * (0.5 + t * 0.4), p.y + s * 0.3 + Math.sin(a) * s * 0.2 - t * s * 1.4, s * 0.4 * (1 - t), k % 2 ? fire.alt : fire.outer, fire.core, 1);
        }
      } else if (e.kind === 'slash') {
        ctx.lineWidth = 4;
        ctx.beginPath();
        const a0 = -2.4 + t * 1.6;
        ctx.arc(p.x, p.y - s * 0.2, s * 0.7, a0, a0 + 1.4);
        ctx.stroke();
      } else {
        ctx.lineWidth = e.kind === 'capture' ? 3 : 4;
        ctx.beginPath();
        ctx.arc(p.x, p.y - s * 0.15, s * (0.3 + t * (e.kind === 'skill' ? 1.6 : 1)), 0, Math.PI * 2);
        ctx.stroke();
        if (e.kind === 'heal' || e.kind === 'capture') {
          for (let k = 0; k < 5; k++) {
            const a = (k / 5) * Math.PI * 2 + t * 2;
            ctx.fillRect(p.x + Math.cos(a) * s * 0.6 - 2, p.y - s * 0.3 - t * s * 0.8 + Math.sin(a) * s * 0.2, 4, 4);
          }
        }
      }
      ctx.restore();
    }
  }

}

export { ROLE_STYLE };

/** マスの集まりの真ん中のマス（周りのマスとの距離の合計が最も小さいもの） */
function centerCell(cells: number[]): number {
  let best = cells[0];
  let bestSum = Infinity;
  for (const c of cells) {
    const sum = cells.reduce((a, x) => a + dist(c, x), 0);
    if (sum < bestSum) (bestSum = sum), (best = c);
  }
  return best;
}
